import {
    TOPIC_CHAT_MESSAGE,
    TOPIC_CHAT_JOIN,
    TOPIC_CHAT_LEAVE,
    TOPIC_LEADER_CONNECTION_STATUS,
    type ConnectionStatus,
} from 'lib/ws/constants'
import type { Service, Hub } from 'lib/ws/election'
import { getWsHub } from 'lib/ws/hub'
import { UIMessage } from 'pbts/c1/api/conversations/v1/conversations'
import {
    ClientMessage,
    JoinConversationMessage,
    LeaveConversationMessage,
    ChatMessage,
    ServerMessage,
} from 'pbts/c1/api/websocketnotify/v1/websocketnotify'

export type OnChatMessageCallback = (message: UIMessage) => void

type JoinRequest = { conversationId: string; tabId: string }
type LeaveRequest = { conversationId: string; tabId: string }

/**
 * ChatService handles chat messaging through the unified WebSocket.
 * Leader tab manages the connection; followers coordinate via Hub.
 */
export class ChatService implements Service {
    public readonly namespace = 'chat'

    // Local tab's subscriptions: conversationId -> callbacks
    private readonly localCallbacks = new Map<string, Set<OnChatMessageCallback>>()

    // Leader only: track which tabs are in which conversations
    private readonly tabConversations = new Map<string, Set<string>>() // conversationId -> tabIds

    // Active conversations for recovery after reconnection
    private readonly activeConversations = new Set<string>()

    private hub: Hub | undefined
    private readonly getAccessToken: () => Promise<string | undefined>
    private sendFunction: ((data: Uint8Array) => void) | undefined
    private isLeaderConnected = false

    // Pending joins to flush when leader connects
    private readonly pendingJoins = new Map<
        string,
        {
            conversationId: string;
            resolve:() => void;
            reject: (err: Error) => void;
            timeoutId: ReturnType<typeof setTimeout>;
            token: string
        }
    >()

    private pendingJoinCounter = 0

    constructor(getAccessToken: () => Promise<string | undefined>) {
        this.getAccessToken = getAccessToken
    }

    public async init(hub: Hub): Promise<void> {
        this.hub = hub

        // All tabs: listen for chat messages from leader
        // Messages are serialized as ArrayBuffer for cross-tab BroadcastChannel compatibility
        hub.subscribe(TOPIC_CHAT_MESSAGE, (buffer: ArrayBuffer) => {
            try {
                const msg = ChatMessage.fromBinary(new Uint8Array(buffer))
                this.handleChatMessage(msg)
            } catch (e) {
                console.error('[ChatService] Failed to deserialize chat message from hub', e)
            }
        })

        // Leader only: handle join/leave requests from followers
        hub.subscribe(TOPIC_CHAT_JOIN, (req: JoinRequest) => {
            if (hub.isLeader()) {
                void this.handleJoinRequest(req)
            }
        })

        hub.subscribe(TOPIC_CHAT_LEAVE, (req: LeaveRequest) => {
            if (hub.isLeader()) {
                this.handleLeaveRequest(req)
            }
        })

        // Listen for connection status
        hub.subscribe(TOPIC_LEADER_CONNECTION_STATUS, (status: { status: ConnectionStatus }) => {
            const wasConnected = this.isLeaderConnected
            this.isLeaderConnected = status.status === 'connected'

            if (this.isLeaderConnected && !wasConnected) {
                // Connection (re)established
                // Leader: onConnectionRecovered() is called directly from NotifyService
                // and handles everything (activeConversations, pendingJoins, tabConversations)
                // Followers: re-announce subscriptions to the leader
                if (!hub.isLeader()) {
                    this.reannounceSubscriptions()
                }
            }
        })
    }

    /**
     * Register the send function from the connection.
     */
    public registerSendFunction(send: (data: Uint8Array) => void): void {
        this.sendFunction = send
    }

    /**
     * Called when connection is lost.
     */
    public onDisconnect(): void {
        this.sendFunction = undefined
        this.isLeaderConnected = false
        // Note: We don't clear pendingJoins here - they'll either timeout or be flushed on reconnect
    }

    /**
     * Called when leader connection recovers.
     * Rejoins all conversations: own subscriptions, queued joins, and follower subscriptions.
     */
    public async onConnectionRecovered(): Promise<void> {
        // Collect all conversations that need to be rejoined:
        // - Our own subscriptions (activeConversations)
        // - Joins that were queued while disconnected (pendingJoins)
        // - Subscriptions from followers (tabConversations)
        const conversationsToJoin = new Set<string>()

        for (const convId of this.activeConversations) {
            conversationsToJoin.add(convId)
        }

        for (const convId of this.pendingJoins.keys()) {
            conversationsToJoin.add(convId)
        }

        for (const convId of this.tabConversations.keys()) {
            conversationsToJoin.add(convId)
        }

        // Clear and resolve pending joins - we're handling everything here
        for (const pending of this.pendingJoins.values()) {
            clearTimeout(pending.timeoutId)
            pending.resolve()
        }
        this.pendingJoins.clear()

        // Rejoin all conversations
        for (const conversationId of conversationsToJoin) {
            await this.sendJoinToBackend(conversationId)
        }
    }

    /**
     * Subscribe to messages for a conversation.
     */
    public async subscribe(conversationId: string, callback: OnChatMessageCallback): Promise<void> {
        // Add to local callbacks
        if (!this.localCallbacks.has(conversationId)) {
            this.localCallbacks.set(conversationId, new Set())
        }
        this.localCallbacks.get(conversationId)?.add(callback)
        this.activeConversations.add(conversationId)

        // Request join
        const hub = this.hub ?? getWsHub()
        if (!hub) {
            return
        }

        if (hub.isLeader()) {
            await this.sendJoinToBackend(conversationId)
        } else {
            hub.publish(TOPIC_CHAT_JOIN, {
                conversationId,
                tabId: hub.getTab().id,
            })
        }
    }

    /**
     * Unsubscribe from messages for a conversation.
     */
    public unsubscribe(conversationId: string, callback: OnChatMessageCallback): void {
        const callbacks = this.localCallbacks.get(conversationId)
        if (callbacks) {
            callbacks.delete(callback)
            if (callbacks.size === 0) {
                this.localCallbacks.delete(conversationId)
                this.activeConversations.delete(conversationId)

                // Request leave
                const hub = this.hub ?? getWsHub()
                if (!hub) return

                if (hub.isLeader()) {
                    // Only send leave if no other followers are subscribed to this conversation
                    const hasFollowers = this.tabConversations.has(conversationId) &&
                        (this.tabConversations.get(conversationId)?.size ?? 0) > 0
                    if (!hasFollowers) {
                        this.sendLeaveToBackend(conversationId)
                    }
                } else {
                    hub.publish(TOPIC_CHAT_LEAVE, {
                        conversationId,
                        tabId: hub.getTab().id,
                    })
                }
            }
        }
    }

    /**
     * Called by leader when a tab closes - cleanup that tab's subscriptions.
     */
    public clearSubscriptionsForTab(tabId: string): void {
        for (const [conversationId, tabs] of this.tabConversations.entries()) {
            tabs.delete(tabId)
            if (tabs.size === 0) {
                this.tabConversations.delete(conversationId)
                // Only send leave if leader is not also subscribed to this conversation
                if (!this.activeConversations.has(conversationId)) {
                    this.sendLeaveToBackend(conversationId)
                }
            }
        }
    }

    /**
     * Called when tab is closing - cleanup subscriptions.
     */
    public onTabClosing(): void {
        const hub = this.hub ?? getWsHub()
        if (!hub) return

        // Clear pending joins to avoid memory leaks
        for (const pending of this.pendingJoins.values()) {
            clearTimeout(pending.timeoutId)
        }
        this.pendingJoins.clear()

        // Announce leave for all active conversations
        for (const conversationId of this.activeConversations) {
            hub.publish(TOPIC_CHAT_LEAVE, {
                conversationId,
                tabId: hub.getTab().id,
            })
        }
    }

    /**
     * Re-announce all active subscriptions to the leader.
     * Called when connection is (re)established and this tab is a follower.
     */
    private reannounceSubscriptions(): void {
        const hub = this.hub ?? getWsHub()
        if (!hub || hub.isLeader()) return

        for (const conversationId of this.activeConversations) {
            hub.publish(TOPIC_CHAT_JOIN, {
                conversationId,
                tabId: hub.getTab().id,
            })
        }
    }

    // ========== Private Methods ==========

    private handleChatMessage(msg: ChatMessage): void {
        const callbacks = this.localCallbacks.get(msg.conversationId)
        if (!callbacks) {
            return
        }

        try {
            const uiMessage = UIMessage.fromBinary(msg.payload)
            callbacks.forEach((cb) => {
                try {
                    cb(uiMessage)
                } catch {
                    // Ignore callback errors
                }
            })
        } catch (e) {
            console.error('[ChatService] Failed to parse message', e)
        }
    }

    private async handleJoinRequest(req: JoinRequest): Promise<void> {
        // Check if conversation is already joined (via leader's own subscription or other followers)
        const alreadyJoined = this.activeConversations.has(req.conversationId) ||
            this.tabConversations.has(req.conversationId)

        // Track this follower tab in the conversation
        if (!this.tabConversations.has(req.conversationId)) {
            this.tabConversations.set(req.conversationId, new Set())
        }
        this.tabConversations.get(req.conversationId)?.add(req.tabId)

        // Only send join to backend if not already joined
        if (!alreadyJoined) {
            await this.sendJoinToBackend(req.conversationId)
        }
    }

    private handleLeaveRequest(req: LeaveRequest): void {
        const tabs = this.tabConversations.get(req.conversationId)
        if (tabs) {
            tabs.delete(req.tabId)
            if (tabs.size === 0) {
                this.tabConversations.delete(req.conversationId)
                // Last tab left - send leave to backend
                this.sendLeaveToBackend(req.conversationId)
            }
        }
    }

    private async sendJoinToBackend(conversationId: string): Promise<void> {
        let accessToken: string | undefined
        try {
            accessToken = await this.getAccessToken()
        } catch (err) {
            console.error(`[ChatService] Failed to get access token for conversation ${conversationId}`, err)
            return
        }
        if (!accessToken) {
            console.warn(`[ChatService] No access token for conversation ${conversationId}, skipping join`)
            return
        }

        if (!this.sendFunction) {
            // Queue for later with timeout to prevent memory leak
            return new Promise((resolve, reject) => {
                // Cancel existing entry for this conversationId to avoid overwrite races
                const existing = this.pendingJoins.get(conversationId)
                if (existing) {
                    clearTimeout(existing.timeoutId)
                    existing.reject(new Error(`Join superseded for conversation ${conversationId}`))
                }

                // Generate unique token for this entry
                const token = `${conversationId}-${++this.pendingJoinCounter}`

                const timeoutId = setTimeout(() => {
                    const current = this.pendingJoins.get(conversationId)
                    // Only remove if token matches (prevent deleting a newer entry)
                    if (current && current.token === token) {
                        this.pendingJoins.delete(conversationId)
                        reject(new Error(`Join timeout for conversation ${conversationId}`))
                    }
                }, 30000) // 30 second timeout

                this.pendingJoins.set(conversationId, { conversationId, resolve, reject, timeoutId, token })
            })
        }

        const clientMessage = ClientMessage.create({
            message: {
                oneofKind: 'joinConversation',
                joinConversation: JoinConversationMessage.create({
                    accessToken,
                    conversationId,
                }),
            },
        })

        this.sendFunction(ClientMessage.toBinary(clientMessage))
    }

    private sendLeaveToBackend(conversationId: string): void {
        if (!this.sendFunction) return

        const clientMessage = ClientMessage.create({
            message: {
                oneofKind: 'leaveConversation',
                leaveConversation: LeaveConversationMessage.create({
                    conversationId,
                }),
            },
        })

        this.sendFunction(ClientMessage.toBinary(clientMessage))
    }

    /**
     * Process a ServerMessage containing a chat message.
     */
    public static processServerMessage(msg: ServerMessage): ChatMessage | undefined {
        if (msg.message.oneofKind === 'chatMessage') {
            return msg.message.chatMessage
        }
        return undefined
    }
}

// Singleton
let chatService: ChatService | undefined
const serviceReadyCallbacks: Array<(service: ChatService) => void> = []

export const getChatService = (): ChatService | undefined => chatService

/**
 * Register a callback to be notified when chat service becomes available.
 * If already available, callback is invoked immediately.
 * Returns an unsubscribe function.
 */
export const onChatServiceReady = (callback: (service: ChatService) => void): (() => void) => {
    if (chatService) {
        callback(chatService)
    } else {
        serviceReadyCallbacks.push(callback)
    }
    return (): void => {
        const idx = serviceReadyCallbacks.indexOf(callback)
        if (idx >= 0) {
            serviceReadyCallbacks.splice(idx, 1)
        }
    }
}

export const initChatService = (getAccessToken: () => Promise<string | undefined>): ChatService => {
    if (!chatService) {
        chatService = new ChatService(getAccessToken)
        // Notify all waiting callbacks
        for (const cb of serviceReadyCallbacks) {
            try {
                cb(chatService)
            } catch {
                // Ignore callback errors
            }
        }
        serviceReadyCallbacks.length = 0
    }
    return chatService
}
